import { isDefined } from "@clipboard-health/util-ts";
import { CardActionArea, CardContent } from "@mui/material";
import { useWorkerProfilePicture } from "@src/appV2/Agents/api/useWorkerProfilePicture";
import { Card, useModalState } from "@src/appV2/redesign/system/ui-components";
import {
  ReviewSource,
  type WorkplaceCommentType,
} from "@src/appV2/redesign/Workplace/Review/api/useWorkplaceComments";
import { useDelayedWorkplaceReviewFlag } from "@src/appV2/redesign/Workplace/Review/useDelayedWorkplaceReviewFlag";
import { parseISO } from "date-fns";

import { CardDelayedLayout } from "./CardDelayedLayout";
import { CardStandardLayout } from "./CardStandardLayout";
import { ConfirmAnonymizeCommentBottomSheet } from "./ConfirmAnonymizeCommentBottomSheet";
import { ConfirmRemoveCommentBottomSheet } from "./ConfirmRemoveCommentBottomSheet";

interface WorkplaceReviewCommentCardProps {
  comment: WorkplaceCommentType;
  workplaceId: string;
  viewerIsAuthor: boolean;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  onLikeChanged: (comment: WorkplaceCommentType) => void;
  onAnonymized: (comment: WorkplaceCommentType) => void;
  onRemoved: (commentId: string) => void;
  children?: React.ReactNode;
}

export function WorkplaceReviewCommentCard(props: WorkplaceReviewCommentCardProps) {
  const {
    comment,
    workplaceId,
    onClick,
    onLikeChanged,
    viewerIsAuthor,
    onAnonymized,
    onRemoved,
    children,
  } = props;
  const { createdAt, workerInfo, source, externalDetails } = comment.attributes;
  const { id: workerId } = workerInfo;

  const isExternalReview = isDefined(source) && source !== ReviewSource.CBH;

  const { useDelayedReviewUI } = useDelayedWorkplaceReviewFlag();
  const hasInlineVideo =
    comment.attributes.type === "VIDEO" && isDefined(comment.attributes.media?.[0]?.url);

  const anonModalState = useModalState();
  const removeModalState = useModalState();
  const { data: profilePicture, isInitialLoading: isProfilePictureLoading } =
    useWorkerProfilePicture(String(workerId), {
      enabled: isDefined(workerId) && !isExternalReview,
    });

  const profilePictureUrl = isExternalReview
    ? externalDetails?.reviewerPhotoUrl
    : profilePicture?.distribution_url;

  const displayDate = parseISO(
    isExternalReview && isDefined(externalDetails?.publishedAt)
      ? externalDetails.publishedAt
      : createdAt
  );

  const layoutProps = {
    comment,
    workplaceId,
    viewerIsAuthor,
    profilePictureUrl,
    isLoadingProfilePicture: isProfilePictureLoading,
    createdAt: displayDate,
    onLikeChanged,
    onAnonymize: () => {
      anonModalState.openModal();
    },
    onRemove: () => {
      removeModalState.openModal();
    },
    reviewUrl: externalDetails?.reviewUrl,
  };

  return (
    <>
      <Card outlined variant="tertiary">
        {hasInlineVideo ? (
          <CardContent
            sx={{
              padding: 7,
              height: "100%",
              minHeight: "11rem",
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
            }}
          >
            {useDelayedReviewUI ? (
              <CardDelayedLayout {...layoutProps} />
            ) : (
              <CardStandardLayout {...layoutProps} />
            )}
          </CardContent>
        ) : (
          <CardActionArea onClick={onClick}>
            <CardContent
              sx={{
                padding: 7,
                height: "100%",
                minHeight: "11rem",
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
              }}
            >
              {useDelayedReviewUI ? (
                <CardDelayedLayout {...layoutProps} />
              ) : (
                <CardStandardLayout {...layoutProps} />
              )}
            </CardContent>
          </CardActionArea>
        )}
        {children}
      </Card>
      {viewerIsAuthor && !isExternalReview && (
        <ConfirmAnonymizeCommentBottomSheet
          modalState={anonModalState}
          commentId={comment.id}
          workplaceId={workplaceId}
          onAnonymized={onAnonymized}
        />
      )}
      {viewerIsAuthor && !isExternalReview && (
        <ConfirmRemoveCommentBottomSheet
          modalState={removeModalState}
          commentId={comment.id}
          workplaceId={workplaceId}
          onRemoved={() => {
            onRemoved(comment.id);
          }}
        />
      )}
    </>
  );
}
